import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import {
  artworkColumnsFormatted,
  artworkColumnsVisibility,
  getArtworkForComplexLink,
} from "@/config/dashboard/artwork";
import { bibliographyColumnsVisibility } from "@/config/dashboard/bibliography";
import { generateTable } from "@/config/dashboard/general";
import { FormLayoutElement } from "@/config/forms/general";
import { Artwork, ArtworkInBibliography, Bibliography } from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import ArtworkInBibliographyDrawer from "./drawer/ArtworkInBibliographyDrawer";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  ArtworkLinkGridCard,
  BibliographyLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";

interface ComplexLinkProps {
  id: string;
  artwork: Artwork | undefined;
  bibliography: Bibliography | undefined;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
}

const columnsForBibliography = [
  {
    accessorKey: "bibliography.bibliographieDescriptionNumeroBibliographie",
    header: "bibliographieDescriptionNumeroBibliographie",
    size: 100,
    cell: (props: any) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<ArtworkInBibliography> }) => {
      return (
        <ImageCell
          value={""}
          attachments={
            row.original.bibliography?.attachments ??
            row.original.attachments ??
            []
          }
          domain={"bibliography"}
        />
      );
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionTitre",
    header: "bibliographieDescriptionTitre",
    size: 150,
    cell: (props: any) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionAnnee",
    header: "bibliographieDescriptionAnnee",
    size: 50,
    cell: (props: any) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
  {
    accessorKey: "bibliography.bibliographieDescriptionAuteur",
    header: "bibliographieDescriptionAuteur",
    size: 150,
    cell: (props: any) => {
      return <TextCell value={String(props.getValue() ?? "")} />;
    },
  },
];

/**
 * ArtworkInBibliographyLinks component
 * @returns {JSX.Element}
 */
const ArtworkInBibliographyLinks = ({
                                      id,
                                      artwork,
                                      fields,
                                      type,
                                      bibliography,
                                    }: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "artworkInBibliography",
    type,
  );

  const { mutate: deleteArtworkInBibliography } = useDeleteComplexLink(
    id,
    "artworkInBibliography",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteArtworkInBibliography({
        id,
        relation: "artworkInBibliography",
      });
    },
    [deleteArtworkInBibliography],
  );

  const [searchContent, setSearchContent] = useState<string>("");
  const renderArtworkInBibliographyRowActions = useCallback(
    (data: ArtworkInBibliography) => {
          const preview =
            type === "bibliography"
              ? {
                  previewType: "artwork" as const,
                  previewId: data.artwork.id,
                  previewElements: [
                    {
                      label: t("oeuvresDescriptionTitre"),
                      value: data.artwork.oeuvresDescriptionTitre,
                      type: "text" as const,
                    },
                    {
                      label: t("oeuvresDescriptionNumeroEnregistrement"),
                      value: data.artwork.oeuvresDescriptionNumeroEnregistrement,
                      type: "text" as const,
                    },
                    {
                      label: t("oeuvresDescriptionResume"),
                      value: data.artwork.oeuvresDescriptionResume,
                      type: "richtext" as const,
                    },
                    {
                      label: t("attachments"),
                      value: data.artwork.attachments,
                      type: "image" as const,
                    },
                  ],
                }
              : type === "artwork"
                ? {
                    previewType: "bibliography" as const,
                    previewId: data.bibliography.id,
                    previewElements: [
                      {
                        label: t("bibliographieDescriptionNumeroBibliographie"),
                        value:
                          data.bibliography
                            .bibliographieDescriptionNumeroBibliographie,
                        type: "text" as const,
                      },
                      {
                        label: t("bibliographieDescriptionResumeLong"),
                        value: data.bibliography.bibliographieDescriptionResumeLong,
                        type: "richtext" as const,
                      },
                      {
                        label: t("attachments"),
                        value:
                          data.bibliography?.attachments ??
                          data.attachments ??
                          [],
                        type: "image" as const,
                      },
                    ],
                  }
                : null;

          if (!preview) return null;

          return (
            <LinkDrawerRowActions
              previewType={preview.previewType}
              previewId={preview.previewId}
              previewElements={preview.previewElements}
              onDelete={() => handleDeleteRelation(data.id)}
              editDrawer={({ open, onOpenChange }) => (
                <ArtworkInBibliographyDrawer
                  key={data.id}
                  fields={fields}
                  data={data}
                  queryId={id}
                  type={type}
                  open={open}
                  onOpenChange={onOpenChange}
                />
              )}
            />
          );
    },
    [t, type, fields, handleDeleteRelation, id],
  );

  const columns: ColumnDef<ArtworkInBibliography, any>[] = useMemo(() => {
    const columnArtwork = getArtworkForComplexLink() as ColumnDef<
      ArtworkInBibliography,
      any
    >[];
    return [
      ...(type === "artwork"
        ? columnsForBibliography.map((column) => ({
          ...column,
          header: t(column.header),
        }))
        : columnArtwork.map((column) => ({
          ...column,
          header: typeof column.header === "string" ? t(column.header) : "",
        }))),
      {
        accessorKey: "oeuvresDansBibliographieDescriptionReproduitPage",
        header: t("oeuvresDansBibliographieDescriptionReproduitPage"),
        size: 50,
      },
      {
        accessorKey: "oeuvresDansBibliographieDescriptionCitePage",
        header: t("oeuvresDansBibliographieDescriptionCitePage"),
        size: 50,
      },
      {
        accessorKey: "oeuvresDansBibliographieDescriptionReproductionCouleur",
        header: t("oeuvresDansBibliographieDescriptionReproductionCouleur"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? t("yes") : t("no")} />;
        },
      },
      {
        accessorKey: "oeuvresDansBibliographieDescriptionNonPagine",
        header: t("oeuvresDansBibliographieDescriptionNonPagine"),
        size: 50,
        cell: (props: any) => {
          return <TextCell value={props.getValue() ? t("yes") : t("no")} />;
        },
      },
      createActionsColumnDef<ArtworkInBibliography>(({ row }) =>
        renderArtworkInBibliographyRowActions(row.original),
      ),
    ];
  }, [fields, handleDeleteRelation, id, t, type]);

  const datatableConf = useMemo(() => {
    const layoutBackLabel = t("relatedRecords");
    const searchInput = (
      <div className="flex items-center gap-2">
        <Input
          placeholder={t("search")}
          className="w-80"
          value={searchContent}
          onChange={(e) => setSearchContent(e.target.value)}
        />
      </div>
    );
    if (type === "artwork") {
      return (
        <ArtworkToBibliographyLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          artwork={artwork}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderArtworkInBibliographyRowActions}
          searchContent={searchContent}
          searchInput={searchInput}
          listGridTitle={t("bibliographiesRelated")}
          layoutBackLabel={layoutBackLabel}
          setIsAddMode={setIsAddMode}
        />
      );
    }
    return (
      <BibliographyToArtworkLinks
        id={id}
        relatedData={relatedData}
        fields={fields}
        bibliography={bibliography}
        isAddMode={isAddMode}
        columnsLink={columns}
        renderRowActions={renderArtworkInBibliographyRowActions}
        searchContent={searchContent}
        searchInput={searchInput}
        listGridTitle={t("artworksRelated")}
        layoutBackLabel={layoutBackLabel}
        setIsAddMode={setIsAddMode}
      />
    );
  }, [
    artwork,
    bibliography,
    columns,
    fields,
    id,
    isAddMode,
    relatedData,
    searchContent,
    setSearchContent,
    type,
    t,
  ]);

  return <div className="w-full">{datatableConf}</div>;
};

type ArtworkToBibliographyLinksProps =
  LinksTableProps<ArtworkInBibliography> & {
  artwork: Artwork | undefined;
};

type BibliographyToArtworkLinksProps =
  LinksTableProps<ArtworkInBibliography> & {
  bibliography: Bibliography | undefined;
};

//Composant pour Oeuvres
const ArtworkToBibliographyLinks = ({
  id,
  relatedData,
  fields,
  artwork,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: ArtworkToBibliographyLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "artwork-links-bibliography-add";
  const STORAGE_KEY_LINKED = "artwork-links-bibliography-linked";
  const defaultVisibilityBib = { ...bibliographyColumnsVisibility, actions: true };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityBib, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityBib };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityBib, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityBib };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notRelatedBibliographies } =
    useGetNotRelatedResource<Bibliography>({
      resourceId: id,
      resultType: "bibliography",
      searchQuery: searchContent,
      pagination: getPagination,
    });

  const columnsBibliographies: ColumnDef<Bibliography, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "bibliographieDescriptionNumeroBibliographie",
        header: t("bibliographieDescriptionNumeroBibliographie"),
        enableResizing: true,
        cell: ({ row }) => {
          return (
            <div className="flex items-center gap-2 text-nowrap">
              <p className="text-[14px] font-[500]">
                {row.original.bibliographieDescriptionNumeroBibliographie}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={
                row.original.attachments ?? []
              }
              domain={"bibliography"}
            />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionTitre",
        header: t("bibliographieDescriptionTitre"),
        size: 100,
        cell: (props) => {
          return <TextCell value={String(props.getValue() ?? "")} />;
        },
      },
      {
        accessorKey: "bibliographieDescriptionAnnee",
        header: t("bibliographieDescriptionAnnee"),
        size: 100,
        cell: ({ row }) => {
          return (
            <TextCell value={row.original.bibliographieDescriptionAnnee} />
          );
        },
      },
      {
        accessorKey: "bibliographieDescriptionAuteur",
        header: t("bibliographieDescriptionAuteur"),
        size: 200,
        cell: (props) => {
          return <TextCell value={String(props.getValue() ?? "")} />;
        },
      },
      createActionsColumnDef<Bibliography>(({ row }) => {
          const data = row.original as Bibliography;
          if (!artwork) return null;
          return (
            <LinkDrawerRowActions
              previewType="bibliography"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("bibliographieDescriptionNumeroBibliographie"),
                  value: data.bibliographieDescriptionNumeroBibliographie,
                  type: "text",
                },
                {
                  label: t("bibliographieDescriptionResumeLong"),
                  value: data.bibliographieDescriptionResumeLong,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <ArtworkInBibliographyDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={artwork.id}
                  type="artwork"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    bibliography: data,
                    artwork: artwork,
                    oeuvresDansBibliographieDescriptionReproduitPage: "",
                    oeuvresDansBibliographieDescriptionReproduitPlanche: "",
                    oeuvresDansBibliographieDescriptionPlancheNonNumerotee: false,
                    oeuvresDansBibliographieDescriptionCitePage: "",
                    oeuvresDansBibliographieDescriptionListePage: "",
                    oeuvresDansBibliographieDescriptionSousNumero: "",
                    oeuvresDansBibliographieDescriptionPhotographe: "",
                    oeuvresDansBibliographieDescriptionReproductionCouleur: false,
                    oeuvresDansBibliographieDescriptionNonPagine: false,
                    oeuvresDansBibliographieDescriptionReproductionCouverture: false,
                    oeuvresDansBibliographieDescriptionTexteLibre: "",
                    oeuvresDansBibliographieNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [artwork, fields, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: relatedData?.length ?? 0,
  };

  const tableOptionsAdd = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibilityAdd,
    onColumnOrderChange: setColumnsOrderAdd,
    state: {
      columnVisibility: columnVisibilityAdd,
      columnOrder: columnsOrderAdd,
    },
  };

  const tableOptionsLinked = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibilityLinked,
    onColumnOrderChange: setColumnsOrderLinked,
    state: {
      columnVisibility: columnVisibilityLinked,
      columnOrder: columnsOrderLinked,
    },
  };

  const addModeTable = (
    <DataTable
      columns={columnsBibliographies}
      data={notRelatedBibliographies?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notRelatedBibliographies?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={tableOptionsAdd}
    />
  );

  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={tableOptionsLinked}
    />
  );

  if (
    listGridTitle != null &&
    setIsAddMode != null &&
    searchInput != null
  ) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <BibliographyLinkGridCard
                  item={row.bibliography}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <ArtworkInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="artwork"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) {
                      setSelectedRelationId(null);
                    }
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }

  return isAddMode ? addModeTable : listTable;
};

// Composant pour Bibliographies
const BibliographyToArtworkLinks = ({
  id,
  relatedData,
  fields,
  bibliography,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: BibliographyToArtworkLinksProps) => {
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 0 }));
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD_BIBLIO = "artwork-links-bibliography-to-artwork-add";
  const STORAGE_KEY_LINKED_BIBLIO = "artwork-links-bibliography-to-artwork-linked";
  const defaultVisibilityArtworks = {
    ...artworkColumnsVisibility,
    oeuvresDescriptionVignette: true,
    oeuvresPublicationStatus: false,
  };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD_BIBLIO);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityArtworks, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityArtworks };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_BIBLIO);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityArtworks, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityArtworks };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD_BIBLIO);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED_BIBLIO);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notAlreadyRelatedArtworks } = useGetNotRelatedResource<Artwork>(
    {
      resourceId: id,
      resultType: "artwork",
      searchQuery: searchContent,
      pagination: getPagination,
    },
  );

  const { t } = useTranslation();

  const columnsArtworks: ColumnDef<Artwork, any>[] = useMemo(() => {
    return [
      ...generateTable(artworkColumnsFormatted, "artwork"),
      createActionsColumnDef<Artwork>(({ row }: { row: Row<Artwork> }) => {
          const data = row.original as Artwork;
          if (!bibliography) return null;
          return (
            <LinkDrawerRowActions
              previewType="artwork"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("oeuvresDescriptionTitre"),
                  value: row.original.oeuvresDescriptionTitre,
                  type: "text",
                },
                {
                  label: t("oeuvresDescriptionNumeroEnregistrement"),
                  value: row.original.oeuvresDescriptionNumeroEnregistrement,
                  type: "text",
                },
                {
                  label: t("oeuvresDescriptionResume"),
                  value: row.original.oeuvresDescriptionResume,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: row.original.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <ArtworkInBibliographyDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={bibliography.id}
                  type="bibliography"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    bibliography: bibliography,
                    artwork: data,
                    oeuvresDansBibliographieDescriptionReproduitPage: "",
                    oeuvresDansBibliographieDescriptionReproduitPlanche: "",
                    oeuvresDansBibliographieDescriptionPlancheNonNumerotee: false,
                    oeuvresDansBibliographieDescriptionCitePage: "",
                    oeuvresDansBibliographieDescriptionListePage: "",
                    oeuvresDansBibliographieDescriptionSousNumero: "",
                    oeuvresDansBibliographieDescriptionPhotographe: "",
                    oeuvresDansBibliographieDescriptionReproductionCouleur: false,
                    oeuvresDansBibliographieDescriptionNonPagine: false,
                    oeuvresDansBibliographieDescriptionReproductionCouverture: false,
                    oeuvresDansBibliographieDescriptionTexteLibre: "",
                    oeuvresDansBibliographieNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [bibliography, fields, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: relatedData?.length ?? 0,
  };

  const tableOptionsAddBib = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibilityAdd,
    onColumnOrderChange: setColumnsOrderAdd,
    state: {
      columnVisibility: columnVisibilityAdd,
      columnOrder: columnsOrderAdd,
    },
  };

  const tableOptionsLinkedBib = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibilityLinked,
    onColumnOrderChange: setColumnsOrderLinked,
    state: {
      columnVisibility: columnVisibilityLinked,
      columnOrder: columnsOrderLinked,
    },
  };

  const addModeTable = (
    <DataTable
      columns={columnsArtworks}
      data={notAlreadyRelatedArtworks?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelatedArtworks?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD_BIBLIO}
      options={tableOptionsAddBib}
    />
  );

  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED_BIBLIO}
      options={tableOptionsLinkedBib}
    />
  );

  if (
    listGridTitle != null &&
    setIsAddMode != null &&
    searchInput != null
  ) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED_BIBLIO}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <ArtworkLinkGridCard
                  artwork={row.artwork}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <ArtworkInBibliographyDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="bibliography"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) {
                      setSelectedRelationId(null);
                    }
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }

  return isAddMode ? addModeTable : listTable;
};

export default ArtworkInBibliographyLinks;
